import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Injection token for Datadog to have an easier time mocking in unit tests
 */
const DATADOG_RUM = new InjectionToken('DatadogRum', {
  providedIn: 'root',
  factory: () => datadogRum
});

/* eslint-disable max-len */
const knownBotPattern = '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';

/**
 * Parses errors and objects to avoid logging strings like "[object]"
 * @param error - can be string, object, array, etc.  See spec for examples & support
 */
function ngkLoggerParseError(error) {
  const fallbackMsg = 'Unknown Error';
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error?.message ? error.message : fallbackMsg;
  }
  if (error instanceof Object) {
    return JSON.stringify(error);
  }
  return fallbackMsg;
}

/**
 * Enables data dog tracking and error handling
 */
let NgKLoggerService = /*#__PURE__*/(() => {
  class NgKLoggerService {
    /** publicly exposed getter for if data dog is initialized for the application */
    get ddIsInitialized() {
      return this._ddIsInitialized;
    }
    constructor(dataDog, injectedWindow) {
      this.dataDog = dataDog;
      this.injectedWindow = injectedWindow;
      this.debug = false;
      /** internal identifier if data dog is initialized for the application */
      this._ddIsInitialized = false;
    }
    /**
     * Enables more logging around events for the session
     */
    enableDebugger() {
      this.debug = true;
      this.debugMsg('debugger enabled!');
    }
    /**
     * Initialize Datadog
     * should only be called once
     */
    initializeDataDog({
      applicationId,
      clientToken,
      service,
      activeUrls,
      site = 'datadoghq.com',
      filterBots = true,
      sessionReplaySampleRate = 100,
      trackUserInteractions = true,
      trackResources = true,
      trackLongTasks = true,
      defaultPrivacyLevel = 'mask-user-input',
      version = undefined,
      ...additionalProps
    }) {
      if (this.ddIsInitialized) {
        this.consoleMsg({
          context: 'Initialize',
          message: 'Datadog is already initialized'
        });
        return;
      }
      // Confirm debugging is off and the host is valid, if not Logger will not init Datadog
      this.env = this.getValidHost(activeUrls);
      if (!this.env) {
        this.debugMsg(`This is not a valid activeUrl: ${this.env}`);
        this.consoleMsg({
          context: 'Initialize',
          message: 'Datadog is not enabled for this environment'
        });
        return;
      }
      // bot detection
      this.debugMsg(`Debug mode is enabled - bot detection is turned ${filterBots ? 'on' : 'off'}.`);
      if (!this.debug && filterBots && this.isKnownBot()) {
        this.consoleMsg({
          context: 'Initialize',
          message: 'Known Bot detected, not initializing Datadog'
        });
        return;
      }
      // connect to data dog
      this.dataDog.init({
        env: this.env,
        silentMultipleInit: true,
        sessionSampleRate: 100,
        applicationId,
        clientToken,
        site,
        service,
        sessionReplaySampleRate,
        trackUserInteractions,
        trackResources,
        trackLongTasks,
        defaultPrivacyLevel,
        version,
        // Include any additional properties
        ...additionalProps
      });
      this._ddIsInitialized = true;
      this.debugMsg('datadog initialized');
      if (sessionReplaySampleRate) {
        this.dataDog.startSessionReplayRecording();
        this.debugMsg('datadog replay recording is on');
      } else {
        this.debugMsg('datadog replay recording is off');
      }
    }
    /**
     * Error Event
     * @description use for errors or issues that are unexpected
     */
    error(eventDetails) {
      let error;
      if (eventDetails.message instanceof Error) {
        const errorName = `${eventDetails.priority}[${eventDetails.context}]`;
        error = eventDetails.message;
        error.name = errorName;
      } else {
        const parsedMsg = ngkLoggerParseError(eventDetails.message);
        const errorMessage = `${eventDetails.priority}[${eventDetails.context}]: ${parsedMsg}`;
        error = new Error(errorMessage);
      }
      if (this.ddIsInitialized) {
        if (eventDetails.additionalInfo) {
          this.dataDog.addError(error, eventDetails.additionalInfo);
        } else {
          this.dataDog.addError(error);
        }
        this.debugMsg(`sent error: ${error.message}`);
      } else {
        this.notSentToDD({
          context: `Error-${eventDetails.priority}-${eventDetails.context}`,
          message: error.message
        });
      }
    }
    /**
     * Analytics Event
     * @description use to track analytics or events that are expected to happen
     */
    event(eventDetails) {
      const metaDataStr = `${eventDetails.name} ${eventDetails.metaData ? JSON.stringify(eventDetails.metaData) : ''}`;
      if (this.ddIsInitialized) {
        this.dataDog.addAction(eventDetails.name, eventDetails.metaData);
        this.debugMsg(`sent event: ${metaDataStr}`);
        // data dog isn't available
      } else {
        this.notSentToDD({
          context: 'Event',
          message: metaDataStr
        });
      }
    }
    /**
     * Uniform way to log messages to the console
     * - this method does not require DD to be Initialized
     */
    consoleMsg(eventDetails) {
      const parsedMsg = ngkLoggerParseError(eventDetails.message);
      console.groupCollapsed('%c🔍 NgK Logger Msg', 'color: #757575; font-size: 12px; font-family: arial');
      console.info(`Context: ${eventDetails.context}`);
      console.info(`%cMessage: ${parsedMsg}`, 'font-weight: bold');
      console.groupEnd();
    }
    /**
     * Private method to warn user about data dog not being initialized
     */
    notSentToDD(eventDetails) {
      const parsedMsg = ngkLoggerParseError(eventDetails.message);
      console.groupCollapsed('%c🔍 NgK Logger [Not Sent to DD]', 'color: #757575; font-size: 12px; font-family: arial');
      console.info(`Context: ${eventDetails.context}`);
      console.info(`%cMessage: ${parsedMsg}`, 'font-weight: bold');
      console.groupEnd();
    }
    /**
     * Private method to log debug messages if enabled
     */
    debugMsg(message) {
      if (this.debug) {
        console.group('%c🔍 NgK Logger [debugger]', 'color: #757575; font-size: 12px; font-family: arial');
        console.info(`%cMessage: ${message}`, 'font-weight: bold');
        console.groupEnd();
      }
    }
    /**
     * Check for a Valid Host URL
     * @returns host string to be used as the env for datadog
     */
    getValidHost(validUrls) {
      const clientHost = this.injectedWindow?.location?.host;
      // if in debug mode, always return host
      if (this.debug) {
        this.debugMsg('Debug mode is enabled - Datadog is enabled for every environment.  **do not leave this on for production**');
        return clientHost ?? 'debug-host';
      }
      // if host is undefined, will return false
      if (!clientHost) {
        this.consoleMsg({
          context: 'Initialize',
          message: 'Unable to determine host url'
        });
        return undefined;
      }
      // check each URL to see if it is valid for dd to log for
      let env;
      if (validUrls) {
        validUrls.forEach(url => {
          if (clientHost.includes(url)) {
            env = url;
          }
        });
      }
      return env;
    }
    /**
     * check if userAgent matches known botPatterns
     * @returns true if userAgent is a bot, false if OK
     * source: https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/
     */
    isKnownBot() {
      if (this.injectedWindow?.navigator) {
        const regex = new RegExp(knownBotPattern, 'i');
        return regex.test(this.injectedWindow?.navigator?.userAgent);
      }
      return false;
    }
    static {
      this.ɵfac = function NgKLoggerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgKLoggerService)(i0.ɵɵinject(DATADOG_RUM), i0.ɵɵinject(WINDOW));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NgKLoggerService,
        factory: NgKLoggerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NgKLoggerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/* eslint-disable  @typescript-eslint/no-unused-vars */
/**
 * Logger service is a unit testing helper to mock the logger functions so no real integrations are fired off
 */
class MockNgKLoggerService {
  constructor() {
    this.ddIsInitialized = false;
  }
  enableDebugger() {}
  initializeDataDog(config) {
    this.ddIsInitialized = true;
  }
  error(event) {}
  event(event) {}
  consoleMsg(event) {}
}
/**
 * Module to help make testing easier
 */
let NgKLoggerServiceTestingModule = /*#__PURE__*/(() => {
  class NgKLoggerServiceTestingModule {
    static {
      this.ɵfac = function NgKLoggerServiceTestingModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgKLoggerServiceTestingModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NgKLoggerServiceTestingModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [{
          provide: NgKLoggerService,
          useClass: MockNgKLoggerService
        }]
      });
    }
  }
  return NgKLoggerServiceTestingModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngk-logger
 */

/**
 * Generated bundle index. Do not edit.
 */

export { MockNgKLoggerService, NgKLoggerService, NgKLoggerServiceTestingModule, ngkLoggerParseError };
